import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import ProtectedRoute from '../../components/ProtectedRoute';
import AdminLayout, { useTheme } from '../../components/AdminLayout';
import { useNotification } from '../../contexts/NotificationContext';

function PremiumRequestsContent() {
  const { t } = useTranslation();
  const { showAlert, showConfirm } = useNotification();
  const { T, S } = useTheme();
  const [memberships, setMemberships] = useState([]);
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [shopFilter, setShopFilter] = useState('');
  const [rejectModal, setRejectModal] = useState(null);
  const [rejectReason, setRejectReason] = useState('');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [memData, shopData] = await Promise.all([
        api.getAdminPremiumMemberships(statusFilter || null, shopFilter || null),
        api.getAdminShops(),
      ]);
      setMemberships(memData);
      setShops(shopData);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, [statusFilter, shopFilter]);

  const shopById = (id) => shops.find(s => s.id === id);

  const pendingCount = memberships.filter(m => m.status === 'pending').length;
  const approvedCount = memberships.filter(m => m.status === 'approved' || m.status === 'active').length;
  const rejectedCount = memberships.filter(m => m.status === 'rejected').length;
  const totalCount = memberships.length;

  const handleApprove = async (id) => {
    showConfirm(t('admin.approveMembershipConfirm', 'Approve this membership request?'), async () => {
      try {
        await api.adminApproveMembership(id, 'approve');
        fetchData();
      } catch (err) { showAlert(err.message); }
    });
  };

  const handleReject = (id) => {
    setRejectModal(id);
    setRejectReason('');
  };

  const confirmReject = async () => {
    try {
      await api.adminApproveMembership(rejectModal, 'reject', rejectReason || null);
      setRejectModal(null);
      fetchData();
    } catch (err) { showAlert(err.message); }
  };

  const kpiCards = [
    { label: t('admin.pendingRequests', 'Pending Requests'), value: pendingCount, color: '#d97706', bg: '#fef3c7' },
    { label: t('admin.approvedThisMonth', 'Approved This Month'), value: approvedCount, color: '#16a34a', bg: '#dcfce7' },
    { label: t('admin.rejected', 'Rejected'), value: rejectedCount, color: '#dc2626', bg: '#fee2e2' },
    { label: t('admin.totalPremiumUsers', 'Total Premium Users'), value: totalCount, color: T.orange, bg: T.bgCard },
  ];

  const statusBadge = (status) => {
    const base = { padding: '0.2rem 0.6rem', borderRadius: '4px', fontSize: '0.72rem', fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.1em', textTransform: 'uppercase' };
    if (status === 'approved' || status === 'active') return { ...base, background: T.green, color: T.greenText };
    if (status === 'rejected') return { ...base, background: T.red, color: T.redText };
    return { ...base, background: T.yellow, color: T.yellowText };
  };

  return (
    <AdminLayout role="admin">
      <div style={{ marginBottom: '2rem' }}>
        <h1 style={S.pageTitle}>{t('admin.premiumMembershipRequests', 'Premium Membership Requests')}</h1>
        <div style={{ width: '40px', height: '2px', background: T.orange, marginBottom: '1.5rem' }} />

        {/* Filters */}
        <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap', marginBottom: '1.5rem' }}>
          <select
            value={shopFilter}
            onChange={e => setShopFilter(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem', marginBottom: 0 }}
          >
            <option value="">{t('admin.allSalons', 'All Salons')}</option>
            {shops.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </select>

          <select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value)}
            style={{ ...S.select, width: '200px', fontSize: '0.82rem', marginBottom: 0 }}
          >
            <option value="">{t('admin.allStatuses', 'All Statuses')}</option>
            <option value="pending">{t('admin.pending', 'Pending')}</option>
            <option value="approved">{t('admin.approved', 'Approved')}</option>
            <option value="rejected">{t('admin.rejected', 'Rejected')}</option>
          </select>

          {(statusFilter || shopFilter) && (
            <button
              onClick={() => { setStatusFilter(''); setShopFilter(''); }}
              style={{ ...S.btnOutline, padding: '0.4rem 0.8rem', fontSize: '0.75rem', height: '36px' }}
            >
              {t('admin.clearFilters', 'Clear Filters')}
            </button>
          )}
        </div>

        {/* KPI Cards */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1rem', marginBottom: '2rem' }}>
          {kpiCards.map((kpi, i) => (
            <div key={i} style={{ ...S.statCard, borderLeft: `4px solid ${kpi.color}` }}>
              <span style={{ ...S.statVal, color: kpi.color }}>{kpi.value}</span>
              <span style={S.statLabel}>{kpi.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Reject Modal */}
      {rejectModal && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <h2 style={S.modalTitle}>{t('admin.rejectMembership', 'Reject Membership')}</h2>
            <label style={{ display: 'block', fontSize: '0.75rem', color: T.muted, letterSpacing: '0.1em', textTransform: 'uppercase', marginBottom: '0.4rem' }}>
              {t('admin.rejectionReason', 'Rejection Reason (optional)')}
            </label>
            <textarea
              value={rejectReason}
              onChange={e => setRejectReason(e.target.value)}
              placeholder={t('admin.enterReason', 'Enter reason...')}
              style={{ ...S.input, minHeight: '80px', resize: 'vertical' }}
            />
            <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
              <button onClick={confirmReject} style={{ ...S.btnOrange, flex: 1, background: T.redText, color: '#fff' }}>
                {t('admin.confirmReject', 'Confirm Reject')}
              </button>
              <button onClick={() => setRejectModal(null)} style={S.btnOutline}>{t('common.cancel', 'Cancel')}</button>
            </div>
          </div>
        </div>
      )}

      {loading ? <p style={{ color: T.muted }}>{t('common.loading', 'Loading...')}</p> : memberships.length === 0 ? (
        <p style={{ color: T.muted, fontStyle: 'italic' }}>{t('admin.noPremiumRequests', 'No premium membership requests found.')}</p>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr>
                {[
                  t('admin.client', 'Client'),
                  t('admin.salon', 'Salon'),
                  t('admin.plan', 'Plan'),
                  t('admin.requestedDate', 'Requested'),
                  t('admin.status', 'Status'),
                  t('admin.actions', 'Actions'),
                ].map(h => <th key={h} style={S.th}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {memberships.map(m => {
                const shop = shopById(m.shop_id);
                return (
                  <tr key={m.id} style={{ transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'rgba(14,165,233,0.04)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={S.td}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                        <div style={{
                          width: '36px', height: '36px', borderRadius: '50%',
                          background: T.orange, color: '#fff', display: 'flex',
                          alignItems: 'center', justifyContent: 'center',
                          fontFamily: "'DM Sans', sans-serif", fontSize: '0.85rem', fontWeight: 700,
                        }}>
                          {(m.user_name || m.user?.name || '?')[0]?.toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight: 600, color: T.white }}>{m.user_name || m.user?.name || `User #${m.user_id}`}</div>
                          <div style={{ fontSize: '0.78rem', color: T.muted }}>{m.user_email || m.user?.email || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ ...S.td, color: T.orange, fontFamily: "'DM Sans', sans-serif", fontSize: '0.82rem', letterSpacing: '0.05em' }}>
                      {shop?.name || `Shop #${m.shop_id}`}
                    </td>
                    <td style={S.td}>
                      <span style={{ fontFamily: "'DM Sans', sans-serif", letterSpacing: '0.05em', textTransform: 'uppercase', fontSize: '0.82rem' }}>
                        {m.plan_name || 'VIP Monthly'}
                      </span>
                      {m.monthly_price && <div style={{ fontSize: '0.75rem', color: T.muted }}>{m.monthly_price} EGP/mo</div>}
                    </td>
                    <td style={S.td}>
                      {m.created_at ? new Date(m.created_at).toLocaleDateString() : '—'}
                    </td>
                    <td style={S.td}>
                      <span style={statusBadge(m.status)}>{m.status}</span>
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: '0.5rem' }}>
                        {m.status === 'pending' && (
                          <>
                            <button
                              onClick={() => handleApprove(m.id)}
                              style={{
                                background: T.greenText, color: '#fff', border: 'none',
                                borderRadius: '4px', padding: '0.35rem 0.85rem',
                                fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem',
                                fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
                              }}
                            >
                              {t('admin.approve', 'Approve')}
                            </button>
                            <button
                              onClick={() => handleReject(m.id)}
                              style={{
                                background: 'transparent', color: T.redText,
                                border: `1px solid ${T.redText}`, borderRadius: '4px',
                                padding: '0.35rem 0.85rem',
                                fontFamily: "'DM Sans', sans-serif", fontSize: '0.75rem',
                                fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', cursor: 'pointer',
                              }}
                            >
                              {t('admin.reject', 'Reject')}
                            </button>
                          </>
                        )}
                        {m.status !== 'pending' && (
                          <span style={{ fontSize: '0.78rem', color: T.muted, fontStyle: 'italic' }}>
                            {m.status === 'rejected' && m.rejection_reason ? m.rejection_reason : '—'}
                          </span>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </AdminLayout>
  );
}

export default function PremiumRequests() {
  return <ProtectedRoute requireSuperAdmin><PremiumRequestsContent /></ProtectedRoute>;
}
